import Head from 'next/head';
import { AdsView } from '@/views/Ads/Ads.view';
import { withPreRenderSettings } from '@/utils/hocs';
import { AdView } from '@/views/Ads/Ad.view';
import { EditAdView } from '@/views/Ads/EditAd.view';
import { getSeoFullData } from '@/api/helpers/seo.helpers';
import { ServerDataProvider } from '@/contexts';
import {
  getNewAdDefaultValues,
  getSearchResultsServerData,
  getServerAdDetails,
} from '@/api/helpers/ad.helpers';
import {
  AdDetails,
  GetAdsResponse,
  NewAdDefaultValues,
} from '@/api/types/ads.types';
import { NewAdView } from '@/views/Ads/NewAd.view';
import NoSsr from '@/components/NoSsr/NoSsr';
import { usePageType } from '@/hooks/usePageType/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import { CATEGORIES_SLUGS_COUNT, ROOT_CATEGORY_SLUG } from '@/constants/common';
import { getCurrentCategory } from '@/api/helpers/categories.helpers';
import { NEW_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import {
  CategoryBaseInfo,
  GetCategoryPropertiesResponse,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { getPlatformSource } from '@/utils/getPlatformSource';
import { PlatformSource } from '@/api/types/common.types';
import { SeoFullData } from '@/api/types/seo.types';
import { GetRegionsResponse } from '@/api/types/regions.types';

interface Props {
  adDetails: AdDetails | null;
  refferer: string;
  newAdDefaultValues: NewAdDefaultValues | null;
  regions: GetRegionsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
  currentCategory: CategoryBaseInfo | null;
  vipAds: GetAdsResponse | null;
  latestAds: GetAdsResponse | null;
  userAds: GetAdsResponse | null;
  categoryProperties: GetCategoryPropertiesResponse | null;
  seoFullData: SeoFullData;
}

function AdsPage({
  adDetails,
  newAdDefaultValues,
  regions,
  rootCategories,
  currentCategory,
  vipAds,
  latestAds,
  userAds,
  categoryProperties,
  seoFullData,
}: Props) {
  const pageType = usePageType();
  const { h1, title } = seoFullData;

  switch (pageType) {
    case PageType.NewAd:
      return (
        <NoSsr>
          <NewAdView {...newAdDefaultValues} />
        </NoSsr>
      );
    case PageType.EditAd:
      return (
        <ServerDataProvider adDetails={adDetails}>
          <EditAdView />
        </ServerDataProvider>
      );
    case PageType.Ad:
      return (
        <ServerDataProvider adDetails={adDetails}>
          <Head>
            <meta
              name="robots"
              content="max-image-preview:large"
              key="max-image-preview"
            />
          </Head>

          <AdView />
        </ServerDataProvider>
      );
    default:
      return (
        <ServerDataProvider
          regions={regions}
          rootCategories={rootCategories}
          currentCategory={currentCategory}
          vipAds={vipAds}
          latestAds={latestAds}
          userAds={userAds}
          categoryProperties={categoryProperties}
          h1={h1 || title}
        >
          <AdsView />
        </ServerDataProvider>
      );
  }
}

AdsPage.getInitialProps = withPreRenderSettings(
  async (context, client, deviceType) => {
    const isBrowser = typeof window !== 'undefined';
    const pathname = context.asPath?.split('?')[0].slice(1) || '';
    const userAgent = context.req?.headers['user-agent'] || '';
    const isTestMode = context.req?.headers['x-test-mode'] === 'true';
    const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
    const platformSource = getPlatformSource(deviceType);
    const source = isSSRWebView ? PlatformSource.App : platformSource;
    const isSearchResultsPage =
      pathname.split('/').length <= CATEGORIES_SLUGS_COUNT &&
      !pathname.endsWith(NEW_AD_PAGE_SLUG);
    const isRootCategory = pathname === ROOT_CATEGORY_SLUG;

    const currentCategory = await getCurrentCategory(client, context);

    const seoFullData = await getSeoFullData(
      client,
      context,
      currentCategory?.id
    );

    if (isSearchResultsPage && !isTestMode) {
      if (!currentCategory?.id && !isRootCategory && context.res) {
        context.res.statusCode = 404;
        return { seoFullData: null, adDetails: null, newAdDefaultValues: null };
      }

      if (isBrowser) {
        return {
          currentCategory: null,
          rootCategories: null,
          vipAds: null,
          latestAds: null,
          seoFullData: seoFullData ?? null,
        };
      }

      const searchResultsData = await getSearchResultsServerData(
        client,
        context,
        currentCategory,
        source
      );

      return {
        currentCategory: currentCategory ?? null,
        regions: searchResultsData?.regions?.data ?? null,
        rootCategories: searchResultsData?.rootCategories?.data ?? null,
        vipAds: searchResultsData?.vipAds?.data ?? null,
        latestAds: searchResultsData?.latestAds?.data ?? null,
        userAds: searchResultsData?.userAds?.data ?? null,
        categoryProperties: searchResultsData?.categoryProperties?.data ?? null,
        seoFullData: seoFullData ?? null,
      };
    }

    const { adDetails } = await getServerAdDetails(client, context, deviceType);
    const newAdDefaultValues = await getNewAdDefaultValues(client, context);

    return {
      seoFullData: seoFullData ?? null,
      adDetails: adDetails ?? null,
      newAdDefaultValues: newAdDefaultValues ?? null,
    };
  }
);

export default AdsPage;
